import { cn, getTestId } from '../utils';
import { cva, type VariantProps } from 'class-variance-authority';
import { Menu, MenuButton, MenuItem, MenuItems, Transition } from '@headlessui/react';
import { IconName } from '../icons/names';
import { Divider } from './Divider';
import { Icon } from './icons/Icon';
import React from 'react';
import { AnchorTo } from '../types/Anchor';

type BaseDropdownMenuAttributes = Pick<
  React.HTMLAttributes<HTMLDivElement>,
  'id' | 'className' | 'onChange'
>;

const DropdownMenuVariants = cva(
  `bg-background-input border !border-border-base rounded-lg shadow-lg absolute z-50 mt-sm overflow-y-hidden`,
  {
    variants: {
      size: {
        sm: 'w-[200px]',
        md: 'w-[240px]',
        lg: 'w-[280px]'
      }
    },
    defaultVariants: {
      size: 'md'
    }
  }
);

type DropdownMenuVariantProps = VariantProps<typeof DropdownMenuVariants>;

/**
 * Base of the dropdown menu
 */
export interface DropdownMenuProps extends BaseDropdownMenuAttributes, DropdownMenuVariantProps {
  triggerButton: React.ReactNode;
  HeadItem?: React.ReactNode;
  onHeadItemClick?: () => void;
  children: React.ReactNode;
  className?: string;
  align?: AnchorTo;
  testId?: string;
}

export const DropdownMenu = React.forwardRef<HTMLDivElement, DropdownMenuProps>(
  (
    {
      triggerButton,
      HeadItem,
      onHeadItemClick,
      children,
      className,
      align = 'bottom',
      size,
      testId,
      id
    },
    ref
  ) => {
    const HeadItemSize = {
      sm: 'w-[198px]',
      md: 'w-[238px]',
      lg: 'w-[278px]'
    }[size ? size : 'md'];

    React.Children.forEach(children, child => {
      if (child === null || child === undefined || child === false || child === true) {
        return;
      }
      if (!React.isValidElement(child) || child.type !== DropdownItem) {
        throw new Error('<Dropdown> only accepts <Dropdown.Item> as children.');
      }
    });

    // A HeadItem-only dropdown (no items) shouldn't render a trailing divider.
    const hasChildren = React.Children.toArray(children).length > 0;

    return (
      <Menu ref={ref}>
        <MenuButton
          aria-haspopup='true'
          aria-expanded='false'
          className={
            React.isValidElement(triggerButton)
              ? (triggerButton.props as { className?: string }).className
              : undefined
          }
          aria-label={
            React.isValidElement(triggerButton)
              ? (triggerButton.props as { 'aria-label'?: string })['aria-label']
              : undefined
          }
        >
          {React.isValidElement(triggerButton)
            ? (triggerButton.props as { children?: React.ReactNode }).children
            : triggerButton}
        </MenuButton>
        <Transition
          enter='transition ease-out duration-200'
          enterFrom='opacity-0 translate-y-1'
          enterTo='opacity-100 translate-y-0'
          leave='transition ease-in duration-150'
          leaveFrom='opacity-100 translate-y-0'
          leaveTo='opacity-0 translate-y-1'
        >
          <MenuItems
            as='div'
            anchor={align}
            data-testid={getTestId({ testId, id })}
            className={cn(className, DropdownMenuVariants({ size }))}
          >
            {HeadItem && (
              <>
                <MenuItem
                  onClick={e => {
                    if (onHeadItemClick) {
                      onHeadItemClick();
                    } else {
                      e.preventDefault();
                      e.stopPropagation();
                    }
                  }}
                  as='div'
                  className={cn(
                    'flex flex-col flex-nowrap justify-start items-center text-md transition rounded',
                    onHeadItemClick && 'cursor-pointer hover:bg-background-muted'
                  )}
                >
                  <div
                    className={cn(
                      'px-md py-md flex flex-row flex-nowrap justify-start items-center',
                      HeadItemSize
                    )}
                  >
                    {HeadItem}
                  </div>
                </MenuItem>
                {hasChildren && <Divider size='full' variant='light' thickness='sm' />}
              </>
            )}

            {children}
          </MenuItems>
        </Transition>
      </Menu>
    );
  }
);

DropdownMenu.displayName = 'DropdownMenu';

/**
 * Dropdown item
 */
type BaseDropdownItemAttributes = Pick<
  React.HTMLAttributes<HTMLDivElement>,
  'id' | 'className' | 'onChange' | 'onFocus' | 'onMouseOver'
>;

export interface DropdownItemProps extends BaseDropdownItemAttributes {
  icon?: IconName;
  HasDivider?: boolean;
  hint?: string;
  disabled?: boolean;
  children: React.ReactNode;
  className?: string;
  testId?: string;
  /** Element-agnostic click handler (item may render as `div`, `a`, or a router `Link`). */
  onClick?: React.MouseEventHandler<HTMLElement>;
  /** Render the item as a link (e.g. a router `Link`); defaults to `'div'`, or `'a'` when `href` is set. */
  as?: React.ElementType;
  /** Native link target. Renders the item as an anchor when no custom `as` is provided. */
  href?: string;
  /** Client-side route target, forwarded to a router `as` component (e.g. `Link`). */
  to?: string;
}

const DropdownItem = React.forwardRef<HTMLElement, DropdownItemProps>(
  (
    {
      as,
      icon,
      HasDivider = false,
      hint,
      children,
      className,
      onClick,
      disabled,
      testId,
      id,
      href,
      to,
      ...props
    },
    ref
  ) => {
    const Component = as ?? (href ? 'a' : 'div');
    const linkProps = as ? { href, to } : href ? { href } : {};
    return (
      <>
        {HasDivider && <Divider size='full' variant='light' thickness='sm' />}
        <MenuItem
          as={Component}
          ref={ref}
          disabled={disabled}
          data-testid={getTestId({ testId, id })}
          onClick={(e: React.MouseEvent<HTMLElement>) => {
            if (disabled) return;
            onClick?.(e);
            // Menu will close automatically after onClick, but we ensure it closes
            // by not calling preventDefault, which allows Headless UI to handle it
          }}
          aria-disabled={disabled}
          className={cn(
            'flex flex-row flex-nowrap justify-between items-center px-md py-sm cursor-pointer text-sm rounded text-inherit no-underline',
            disabled && 'opacity-50 cursor-not-allowed',
            !disabled && 'hover:bg-background-muted transition duration-300',
            className
          )}
          {...linkProps}
          {...props}
        >
          <div className='flex items-center gap-sm font-normal'>
            {icon && <Icon name={icon} className='mb-2xs' />}
            {children}
          </div>
          {hint && (
            <div className='ml-md'>
              <p className='text-xs text-foreground-muted font-normal'>{hint}</p>
            </div>
          )}
        </MenuItem>
      </>
    );
  }
);

DropdownItem.displayName = 'DropdownItem';

const Dropdown = Object.assign(DropdownMenu, {
  Item: DropdownItem
});

export { Dropdown };
